import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useState,
} from "react";
import DataGrid, {
  Editing,
  Column,
  Button,
  FilterRow,
  HeaderFilter,
  Paging,
  Pager,
  ColumnChooser,
  Scrolling,
} from "devextreme-react/data-grid";

const CustomDataGrid = forwardRef(
  (
    {
      dataSource,
      tableId,
      rowAlternationEnabled,
      showFilterRow,
      currentFilter,
      showHeaderFilter,
      selectionChanged,
      columns,
      buttonColumn,
      buttonColumnBudget,
      budgetPage,
      remoteOperations,
      loadMyOptions,
    },
    ref
  ) => {
    const dataGridRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [budgetColumns, setBudgetColumns] = useState([]);

    useEffect(() => {

      if(budgetPage){
        const storedColumnOrder = localStorage.getItem('columnOrder');
        if (storedColumnOrder) {
        const parsedColumnOrder = JSON.parse(storedColumnOrder);
        // Get the column objects using the dataField
        const columnsToReorder = parsedColumnOrder.map(dataField => columns.find(column => column.dataField === dataField));

        setBudgetColumns(columnsToReorder)

      }else{
       // Get the column objects using the dataField from the original budget list order
        const columnOrder = columns.map(column => column.dataField);

        localStorage.setItem('columnOrder', JSON.stringify(columnOrder));
        setBudgetColumns(columns)
      }}
     
      
      //targeting the last li element of column chooser and avoid it from being rendered because this li is empty and have no use.
      const styleElement = document.createElement("style");
      styleElement.textContent = `
        li[data-item-id="17"] {
          display: none !important;
        }
      `;
  

      document.head.appendChild(styleElement);

      //target the button
      const columnChooserElement = document.querySelector('.dx-button-content');

      //target the dropdown columns list
      const gridColumns = document.querySelectorAll('.dx-datagrid-column-chooser .dx-overlay-content');

      gridColumns.forEach(gridColumn => {
        gridColumn.style.display = "none";
      });

      const handleColumnChooserClick = () => {
        // Perform any desired actions
        gridColumns.forEach(gridColumn => {
          gridColumn.style.display = "block";
        });
      };

      if (columnChooserElement) {
        columnChooserElement.addEventListener('click', handleColumnChooserClick);
      }
  
      const handleOutsideClick = (event) => {
        //target everything exept the button and the dropdownlist
        const ignoredClassNames = ['.dx-overlay-content', '.dx-button-content'];

        const isIgnored = ignoredClassNames.some(className => {
          return event.target.closest(className);
        });

        if (!isIgnored) {
          // Perform any desired actions
          gridColumns.forEach(gridColumn => {
            gridColumn.style.display = "none";
          });
        }
      };
  
      document.addEventListener('click', handleOutsideClick);

      return () => {
        document.head.removeChild(styleElement);

        if (columnChooserElement) {
          columnChooserElement.removeEventListener('click', handleColumnChooserClick);
        }

        document.removeEventListener('click', handleOutsideClick);

        gridColumns.forEach(gridColumn => {
          gridColumn.style.display = "none";
        });
      };
    }, [isLoading, setBudgetColumns]);

    const useRefresh = () => {
      dataGridRef.current.instance.refresh();
    };

    const clearFilter = () => {
      dataGridRef.current.instance.clearFilter();
    };

    //to distplay the columnChooser as soon as the table is ready not before!
    const handleContentReady = () => {
      setIsLoading(true);
    };


    const handleColumnReorder = (e) => {
      // debugger
     if(budgetColumns.length!==0){
      const dataGrid = e.component;

        const columnOrder = dataGrid.getVisibleColumns().map(column => column.dataField);
        const columnOrderWithoutLast = columnOrder.slice(0, -1);
        localStorage.setItem('columnOrder', JSON.stringify(columnOrderWithoutLast));

     } 
    };

    useImperativeHandle(ref, () => ({
      useRefresh,
      clearFilter,
    }));

    return (
      <DataGrid
        dataSource={dataSource}
        onContentReady={handleContentReady}
        ref={dataGridRef}
        keyExpr={tableId}
        showBorders={false}
        showColumnLines={false}
        showRowLines={false}
        allowColumnReordering={budgetPage}
        onOptionChanged={handleColumnReorder}
        rowAlternationEnabled={rowAlternationEnabled}
        onSelectionChanged={selectionChanged}
        remoteOperations={remoteOperations}
      >
        {budgetPage && (
          <Scrolling
            useNative={true}
            scrollByContent={true}
            scrollByThumb={true}
            style={{ zIndex: "999" }}
            showScrollbar="always"
          />
        )}

        <Pager
          visible={true}
          displayMode="full"
          showPageSizeSelector={true}
          showInfo={true}
          showNavigationButtons={true}
          allowedPageSizes={[5, 10, 20, 30, 40, 50]}
        />

        <Paging defaultPageSize={30} />

        <FilterRow visible={showFilterRow} applyFilter={currentFilter} />

        <Editing allowUpdating={true} allowDeleting={true} />
        <HeaderFilter visible={showHeaderFilter} />

        {budgetPage && isLoading && (
                <ColumnChooser
                  enabled={true}
                  allowSelectAll={true}
                  mode="select"
                />
        )}

        { !budgetPage && columns.map((column) => (
          <Column
            key={column.dataField}
            dataField={column.dataField}
            caption={column.caption}
            width={column.width}
            type={column.type}
            visible={column.visible}
            alignment={column.alignment}
            minWidth={column.minWidth}
            placeholder={column.placeholder}
            allowFiltering={column.allowFiltering}
            allowSorting={column.allowSorting}
            customizeText={column.customizeText}
            cellRender={column.cellRender}
          />
        ))}

        {budgetPage && budgetColumns!= undefined && budgetColumns.map((column) => (
          <Column
            key={column.dataField}
            dataField={column.dataField}
            caption={column.caption}
            width={column.width}
            type={column.type}
            visible={column.visible}
            alignment={column.alignment}
            minWidth={column.minWidth}
            placeholder={column.placeholder}
            allowFiltering={column.allowFiltering}
            allowSorting={column.allowSorting}
            customizeText={column.customizeText}
            cellRender={column.cellRender}
          />
        ))}

        {buttonColumn && (
          <Column type="buttons" width={100}>
            {buttonColumn.map((button) => (
              <Button
                type="buttons"
                text={button.text}
                icon={button.icon}
                hint={button.hint}
                className={button.className}
                style={button.style}
                visible={button.visible}
                name={button.name}
                onClick={button.onClick}
                disabled={button.disabled}
              />
            ))}
          </Column>
        )}

        {buttonColumnBudget && (
          <Column
            fixed={true}
            type="buttons"
            width="130px"
            fixedPosition="right"
          >
            {buttonColumnBudget.map((button) => (
              <Button
                type="buttons"
                text={button.text}
                icon={button.icon}
                hint={button.hint}
                className={button.className}
                style={button.style}
                visible={button.visible}
                name={button.name}
                onClick={button.onClick}
                disabled={button.disabled}
              />
            ))}
          </Column>
        )}
      </DataGrid>
    );
  }
);

export default CustomDataGrid;
